import { defineStore } from 'pinia'
import { addDays, format, parse } from 'date-fns'
import { nanoid } from 'nanoid'
import { DATE_FORMAT } from '../components/SearchPanel/SearchPanelForm/SearchPanelForm.constants'
import type { InterestCity } from '../types/model/interestCity'
import { useBasePassengerInfo } from '../composables/useBasePassengerInfo'
import { getOrderedActiveSearchType } from '@/modules/Search/utils'
import {
  type SearchPanel,
  type SearchPanelDestinationItems,
  type SearchPanelFlight,
  type SearchPanelMain,
  SearchTravelModes,
  SearchTripType,
  SearchType,
} from '@/modules/Search/types'
import useAuthStore from '@/modules/Auth/store'
import type { Airport } from '@/modules/Profile/types/model/airport'
import featureFlag from '@/config/featureFlag'

const basePassengerInfo = useBasePassengerInfo()

export const INITIAL_STATE: SearchPanel = {
  searchId: undefined,
  guestId: nanoid(),
  main: {
    tripType: SearchTripType.RoundTrip,
    arrivalTime: '',
    departureTime: '',
    attributesDestination: undefined,
    origin: undefined,
    destination: undefined,
    bags: 'null',
    passengersWithRoom: [basePassengerInfo],
    searchBy:
      (getOrderedActiveSearchType(featureFlag)[0] as SearchType) ||
      SearchType.Destination,
    modes: [SearchTravelModes.Flight],
    interestsAndActivities: [],
    eventStartTime: '',
    eventEndTime: '',
    eventDestination: undefined,
    visaCountry: undefined,
    accommodationDestination: undefined,
    destinationCode: undefined,
    nationality: undefined,
    flights: [],
  },
  destinations: [],
  interestsAndActivities: [],
  attributeOptions: [],
}

const KEY = 'searchPanel'

const useSearchPanelStore = defineStore(KEY, {
  state: () => ({ ...INITIAL_STATE }),
  getters: {
    getId: (state) => {
      const authStore = useAuthStore()
      return authStore.loggedIn ? authStore.user.id : state.guestId
    },
    roundTripIsOnTheSameDate: (state) =>
      state.main.departureTime === state.main.arrivalTime,
    isAttributeOptionSelected: (state) => (payload: string) => {
      return !!state.attributeOptions.find(
        ({ geoLocation }) => payload === geoLocation
      )
    },
    attributeLocation: (state) => {
      const attr = state.main.attributesDestination

      if (!attr) {
        return
      }

      if (attr.locationType === 'Country' && attr.geoCountry) {
        return attr.geoCountry
      } else if (attr.locationType === 'Continent' && attr.geoContinent) {
        return attr.geoContinent
      } else {
        return attr.geoCity as string
      }
    },
    isMultiDestination: (state) =>
      !!state.destinations.length &&
      state.main.searchBy !== SearchType.Attributes,
    isOneWay: (state) => state.main.tripType === SearchTripType.OneWay,
    isRoundTrip: (state) => state.main.tripType === SearchTripType.RoundTrip,
    isMultiCity: (state) => state.main.tripType === SearchTripType.MultiCity,
    isMultiCityValid: (state) => {
      if (state.main.tripType !== SearchTripType.MultiCity) {
        return true
      }

      return (
        state.destinations.length >= 1 &&
        state.destinations.every(
          (destination) =>
            destination.origin?.geoLocation &&
            destination.destination?.geoLocation &&
            destination.departureDate
        )
      )
    },
    isSearchByDestination: (state) =>
      state.main.searchBy === SearchType.Destination,
    isSearchByAttributes: (state) =>
      state.main.searchBy === SearchType.Attributes,
    isSearchByEvents: (state) => state.main.searchBy === SearchType.Events,
    isSearchByVisa: (state) => state.main.searchBy === SearchType.Visa,
    isSearchByAccommodation: (state) =>
      state.main.searchBy === SearchType.Accommodation,
    isSearchByPackage: (state) => state.main.searchBy === SearchType.Package,
    isFlightModeOnly: (state) =>
      state.main.modes.includes(SearchTravelModes.Flight) &&
      state.main.modes.length === 1,
    isHotelMode: (state) => state.main.modes.includes(SearchTravelModes.Hotel),
    isCarMode: (state) => state.main.modes.includes(SearchTravelModes.Car),
    shouldUseDateRange(): boolean {
      if (this.isSearchByAccommodation || this.isSearchByEvents) {
        return true
      }

      if (this.isSearchByVisa || this.isSearchByPackage) {
        return false
      }

      if (this.isRoundTrip) {
        return true
      }

      if (this.isOneWay || this.isMultiCity) {
        if (this.isFlightModeOnly) {
          return false
        }

        return this.isHotelMode || this.isCarMode
      }

      return false
    },
    destinationList: (state) => {
      const isSearchByDestination =
        state.main.searchBy === SearchType.Destination
      const isSearchEvents = state.main.searchBy === SearchType.Events
      const isMultiCity = state.main.tripType === SearchTripType.MultiCity

      if (isMultiCity) {
        return state.destinations.map(
          ({ departureDate, arrivalDate, destination, origin }) => ({
            origin,
            destination,
            departureDate,
            arrivalDate,
          })
        )
      }

      const mainDestination = {
        origin: state.main.origin,
        ...(isSearchByDestination
          ? { destination: state.main.destination }
          : isSearchEvents
          ? { destination: state.main.eventDestination }
          : { destination: state.main.attributesDestination }),
        departureTime: state.main.departureTime,
        arrivalTime: state.main.arrivalTime,
      }

      const destinations: SearchPanelDestinationItems = [
        mainDestination,
        ...state.destinations.map(
          ({ departureDate, arrivalDate, destination, origin }) => ({
            origin,
            destination,
            departureDate,
            arrivalDate,
          })
        ),
      ]

      return destinations
    },
    roundTripDestinationList(): SearchPanelDestinationItems {
      const roundTripDestinationList = [...this.destinationList]

      roundTripDestinationList.push({
        origin:
          roundTripDestinationList[roundTripDestinationList.length - 1]
            .destination,
        destination: roundTripDestinationList[0].origin,
        departureDate:
          roundTripDestinationList[roundTripDestinationList.length - 1]
            .arrivalDate ?? '',
        arrivalDate:
          roundTripDestinationList[roundTripDestinationList.length - 1]
            .arrivalDate ?? '',
      })

      return roundTripDestinationList
    },
    totalAdultPassengers: (state) => {
      return state.main.passengersWithRoom.reduce(
        (acc, { adults }) => acc + adults,
        0
      )
    },
    totalChildPassengers: (state) => {
      return state.main.passengersWithRoom.reduce(
        (acc, { children }) => acc + children,
        0
      )
    },
    totalPassengers(): number {
      return (
        Number(this.totalAdultPassengers) + Number(this.totalChildPassengers)
      )
    },
  },
  actions: {
    setSearchId(payload: string) {
      this.searchId = payload
    },
    setMain(payload: Partial<SearchPanelMain>) {
      this.main = {
        ...this.main,
        ...payload,
      }
    },
    setInterestsAndActivities(payload: string[]) {
      this.interestsAndActivities = payload
    },
    addDestination(payload: SearchPanelFlight) {
      this.destinations.push(payload)
    },
    removeDestination(index: number) {
      this.destinations.splice(index, 1)
    },
    resetDestinations() {
      this.destinations = []
    },
    updateDestinationsDate(payload: string) {
      if (!this.destinations.length) {
        return
      }

      this.destinations[0].departureDate = payload

      for (let i = 0; i < this.destinations.length - 1; i++) {
        const prevArrival = this.destinations[i].arrivalDate
        const newDate =
          prevArrival ||
          format(
            addDays(
              parse(
                this.destinations[i].departureDate,
                DATE_FORMAT,
                new Date()
              ),
              1
            ),
            DATE_FORMAT
          )

        this.destinations[i + 1].departureDate = newDate
      }
    },
    setDestinations(payload: SearchPanelFlight[]) {
      this.destinations = [...payload]
    },
    selectAttributeOption(payload: InterestCity) {
      const foundIndex = this.attributeOptions.findIndex(
        ({ geoLocation }) => payload.geoLocation === geoLocation
      )

      if (foundIndex >= 0) {
        this.attributeOptions.splice(foundIndex, 1)

        return
      }

      this.attributeOptions.push(payload)
    },
    setAttributeOptions(payload: Array<InterestCity>) {
      this.attributeOptions = payload
    },
    clearAttributeOptions() {
      this.attributeOptions = []
    },
    setPayload(payload: Omit<SearchPanel, 'searchId' | 'guestId'>) {
      this.setMain(payload.main)
      this.setDestinations(payload.destinations)
      this.attributeOptions = payload.attributeOptions
      this.setInterestsAndActivities(payload.interestsAndActivities)
    },
    setSearchBy(searchBy: SearchType) {
      this.setMain({ searchBy })
    },
    setEventDestination(payload: Airport) {
      this.main.eventDestination = payload
    },
    appendRoomToPassengers() {
      this.main.passengersWithRoom = [
        ...this.main.passengersWithRoom,
        { ...basePassengerInfo },
      ]
    },
    removeLastPassengerRoom() {
      this.main.passengersWithRoom = this.main.passengersWithRoom.slice(0, -1)
    },
    reset() {
      this.$reset()
    },
  },
  persist: {
    storage: window.localStorage,
    paths: ['searchId'],
  },
})

export default useSearchPanelStore
